import { useState, useEffect, useMemo } from 'react';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import {
  loadInterestRegistrations,
  recalculateInterestCounts,
  applyInterestCounts,
} from '../lib/interestStore';

function deriveFlags(d) {
  const ios =
    d.platform_ios === 1 || d.platform === 'ios' || d.platform === 'both'
      ? 1
      : 0;
  const android =
    d.platform_android === 1 ||
    d.platform === 'android' ||
    d.platform === 'both'
      ? 1
      : 0;
  return { ios, android };
}

export default function InterestRegistrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Recalculate state
  const [preview, setPreview] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [applying, setApplying] = useState(false);

  function loadData() {
    setLoading(true);
    setError(null);
    loadInterestRegistrations()
      .then(setRows)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(loadData, []);

  // Compute stats
  const stats = useMemo(() => {
    const clubCounts = {};
    let iosCount = 0;
    let androidCount = 0;
    for (const d of rows) {
      const cid = d.club_id || 'unknown';
      clubCounts[cid] = (clubCounts[cid] || 0) + 1;
      const { ios, android } = deriveFlags(d);
      iosCount += ios;
      androidCount += android;
    }
    return { total: rows.length, iosCount, androidCount, clubCounts };
  }, [rows]);

  async function handleRecalculate() {
    setRecalculating(true);
    setError(null);
    try {
      const result = await recalculateInterestCounts();
      setPreview(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setRecalculating(false);
    }
  }

  async function handleApply(onlyIncreases) {
    if (!preview) return;
    const count = preview.filter((r) =>
      onlyIncreases ? r.diff > 0 : r.diff !== 0,
    ).length;
    if (!count) return;
    if (!window.confirm(
      `Apply ${count} update(s)${onlyIncreases ? ' (increases only)' : ''}?`,
    )) return;
    setApplying(true);
    try {
      await applyInterestCounts(preview, onlyIncreases);
      setPreview(null);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  }

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/admin" backLabel="Admin" title="Interest Registrations" />

      {error && <div className="error-banner">{error}</div>}

      {/* Stats + Recalculate button */}
      {!loading && rows.length > 0 && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ flex: 1, display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <span><strong>Total:</strong> {stats.total}</span>
            <span><strong>iOS:</strong> {stats.iosCount}</span>
            <span><strong>Android:</strong> {stats.androidCount}</span>
            {Object.keys(stats.clubCounts).sort().map((cid) => (
              <span key={cid}><strong>{cid}:</strong> {stats.clubCounts[cid]}</span>
            ))}
          </div>
          <button
            className="btn btn-primary"
            onClick={handleRecalculate}
            disabled={recalculating}
          >
            {recalculating ? 'Calculating...' : 'Recalculate Counts'}
          </button>
        </div>
      )}

      {/* Recalculate preview */}
      {preview && (
        <div className="section" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <strong>Preview:</strong> Review changes before applying.
            <button className="btn btn-primary btn-sm" onClick={() => handleApply(false)} disabled={applying}>
              Apply All
            </button>
            <button className="btn btn-sm" style={{ background: '#2e7d32', color: '#fff', borderColor: '#2e7d32' }} onClick={() => handleApply(true)} disabled={applying}>
              Only Increases
            </button>
            <button className="btn btn-sm" onClick={() => setPreview(null)} disabled={applying}>
              Cancel
            </button>
          </div>
          <table className="config-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>ID</th>
                <th style={{ textAlign: 'right' }}>Current</th>
                <th style={{ textAlign: 'right' }}>Calculated</th>
                <th style={{ textAlign: 'right' }}>Diff</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((row) => {
                const diffColor = row.diff > 0 ? '#2e7d32' : row.diff < 0 ? '#c62828' : '#888';
                const bg = row.diff > 0 ? '#e8f5e9' : row.diff < 0 ? '#ffebee' : undefined;
                return (
                  <tr key={`${row.type}-${row.id}`} style={{ background: bg }}>
                    <td>{row.type}</td>
                    <td><code>{row.id}</code></td>
                    <td style={{ textAlign: 'right' }}>{row.current}</td>
                    <td style={{ textAlign: 'right' }}>{row.calculated}</td>
                    <td style={{ textAlign: 'right', color: diffColor, fontWeight: 600 }}>
                      {row.diff > 0 ? `+${row.diff}` : row.diff}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Registrations table */}
      {loading && <p>Loading...</p>}
      {!loading && rows.length === 0 && !error && (
        <p className="empty-state">No registrations yet.</p>
      )}
      {!loading && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="config-table" style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th>User</th>
                <th>Club</th>
                <th>Event</th>
                <th>Fleet/Class</th>
                <th style={{ textAlign: 'center' }}>iOS</th>
                <th style={{ textAlign: 'center' }}>Android</th>
                <th>Source</th>
                <th>Registered</th>
                <th style={{ textAlign: 'center' }}>Taps</th>
                <th style={{ textAlign: 'center' }}>Converted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const { ios, android } = deriveFlags(d);
                const fleet =
                  d.fleet_class ||
                  (d.class_ids && d.class_ids.length
                    ? d.class_ids.join(', ')
                    : '\u2014');
                const source = d.source || (d.event_id ? 'app' : '\u2014');
                const ts = d.registered_at
                  ? d.registered_at.toDate().toLocaleDateString('en-IE')
                  : '\u2014';
                return (
                  <tr key={d.id}>
                    <td>{d.user_email || d.user_id || '\u2014'}</td>
                    <td>{d.club_id || '\u2014'}</td>
                    <td>{d.event_id || '\u2014'}</td>
                    <td>{fleet}</td>
                    <td style={{ textAlign: 'center' }}>{ios}</td>
                    <td style={{ textAlign: 'center' }}>{android}</td>
                    <td>{source}</td>
                    <td>{ts}</td>
                    <td style={{ textAlign: 'center' }}>{d.tap_count || 0}</td>
                    <td style={{ textAlign: 'center' }}>{d.converted ? 'Yes' : 'No'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </>
  );
}
